/**
 * Mobile presentation for the list screens.
 *
 * Every list in the app is a wide table. On a phone a table can only scroll
 * sideways, which hides the columns that matter. Instead each page renders its
 * table inside <TableWrap> (visible from `md` up) and the same rows through
 * <CardList>/<DataCard> below `md`, so nothing is hidden and nothing scrolls
 * horizontally.
 */

/** Desktop table container — hidden on phones, horizontally scrollable above. */
export const TableWrap = ({ children, testid }) => (
  <div className="hidden md:block overflow-x-auto rounded-md border border-slate-200 bg-white" data-testid={testid}>
    {children}
  </div>
);

export const CardList = ({ children, empty, testid }) => {
  const items = Array.isArray(children) ? children.filter(Boolean) : children;
  const isEmpty = !items || (Array.isArray(items) && items.length === 0);
  return (
    <div className="space-y-2 md:hidden" data-testid={testid}>
      {isEmpty ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-white px-4 py-12 text-center text-sm text-slate-500">
          {empty || "Nothing here yet"}
        </div>
      ) : (
        items
      )}
    </div>
  );
};

/**
 * One record as a card.
 *  title/subtitle — the identifying line
 *  right          — status chip or amount, top-right
 *  rows           — [label, value] pairs; falsy entries are skipped
 *  actions        — buttons along the bottom
 *  onClick        — makes the whole card tappable
 */
export const DataCard = ({ title, subtitle, right, rows = [], actions, onClick, testid }) => {
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      {...(onClick ? { onClick, type: "button" } : {})}
      data-testid={testid}
      className={`w-full rounded-md border border-slate-200 bg-white p-3.5 text-left ${
        onClick ? "transition-colors duration-150 active:bg-slate-50" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-slate-900">{title}</div>
          {subtitle && <div className="mt-0.5 truncate text-[11px] text-slate-500">{subtitle}</div>}
        </div>
        {right && <div className="shrink-0 text-right">{right}</div>}
      </div>

      {!!rows.filter(Boolean).length && (
        <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-100 pt-3">
          {rows.filter(Boolean).map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-[10px] uppercase tracking-wide text-slate-400">{label}</dt>
              <dd className="truncate text-xs text-slate-800">{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {actions && (
        <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-3">
          {actions}
        </div>
      )}
    </Wrapper>
  );
};

export default DataCard;
